import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Shield, Users, FileText, Settings, Save, RefreshCw, ShoppingCart, Package, UserPlus, ChevronRight, Building2, Plus, Trash2, Check, X, Truck, Receipt, Database, Tag, Folder, Menu, Edit, Loader2, Store, Warehouse, FileCheck, UserCog, Upload, Clock, Layers, Calculator, Image as ImageIcon } from 'lucide-react';
import CompanyProfileForm from '@/components/CompanyProfileForm';
import EmployeeManagement from '@/components/EmployeeManagement';
import MasterBulkUpload from '@/components/MasterBulkUpload';
import MasterUploadHistory from '@/components/MasterUploadHistory';
import MasterDataSection from '@/components/MasterDataSection';
import DatabaseCleanup from '@/components/DatabaseCleanup';
import OrderStatusRepair from '@/components/OrderStatusRepair';
import { DepartmentModal } from '@/components/admin/DepartmentModal';
import { MasterDataModal } from '@/components/admin/MasterDataModal';
import MarginMasterAdmin from '@/components/MarginMasterAdmin';
import StoreLoginsAdmin from '@/components/StoreLoginsAdmin';
import CategoryImagesAdmin from '@/components/CategoryImagesAdmin';
import POApproversSettings from '@/components/POApproversSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_CONFIG = {
  products: { name: 'Master Catalogue', icon: Package, color: 'blue' },
  suppliers: { name: 'Supplier Master', icon: Users, color: 'purple' },
  onboarding: { name: 'Onboarding', icon: UserPlus, color: 'green' },
  purchase_order: { name: 'Purchase Orders', icon: ShoppingCart, color: 'orange' },
  grn: { name: 'GRN Module', icon: Truck, color: 'teal' },
  inventory: { name: 'Inventory Management', icon: Warehouse, color: 'indigo' },
  invoices: { name: 'Invoices', icon: FileCheck, color: 'emerald' },
  stock_ledger: { name: 'Stock Ledger', icon: FileText, color: 'gray' },
  sales_order: { name: 'Sales Orders', icon: Receipt, color: 'pink' },
};

const TAB_LABELS = {
  header: 'Header Details',
  items: 'Product Items',
  tax_summary: 'Tax & Summary',
  attachments: 'Attachments',
  grn: 'GRN / Receiving',
};

const ACTION_LABELS = {
  create: 'Create PO',
  edit: 'Edit PO',
  view: 'View PO',
  approve: 'Approve PO',
  receive_grn: 'Receive Stock (GRN)',
  cancel: 'Cancel PO',
  delete: 'Delete PO',
};

function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [fieldConfigs, setFieldConfigs] = useState({});
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptModuleAccess, setDeptModuleAccess] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedModule, setSelectedModule] = useState('products');
  const [availableModules, setAvailableModules] = useState([]);

  // Memoised slice — role selector is rendered on every tab/state change;
  // filter was previously recomputed per render.
  const nonAdminRoles = useMemo(
    () => roles.filter(r => r.name !== 'admin'),
    [roles],
  );
  
  // Department modal
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '', color: '#3B82F6' });
  
  // Master Data Management
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterSubBrands, setMasterSubBrands] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterMenu, setMasterMenu] = useState([]);
  const [masterSubMenu, setMasterSubMenu] = useState([]);
  const [masterCompanies, setMasterCompanies] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [masterModalType, setMasterModalType] = useState('brand'); // brand, sub_brand, category, menu, sub_menu, company
  const [editingMaster, setEditingMaster] = useState(null);
  const [masterForm, setMasterForm] = useState({ name: '', description: '', is_active: true, brand_id: '' });
  const [savingMaster, setSavingMaster] = useState(false);
  
  // Bulk Upload State
  const [bulkUploadType, setBulkUploadType] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  async function fetchAllData() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      // Initialize RBAC and enhanced configs
      await axios.get(API + '/admin/init-rbac', { headers });
      await axios.get(API + '/admin/init-enhanced-configs', { headers });
      
      const [rolesRes, permsRes, fieldsRes, usersRes, deptsRes, deptAccessRes] = await Promise.all([
        axios.get(API + '/admin/roles', { headers }),
        axios.get(API + '/admin/permissions', { headers }),
        axios.get(API + '/admin/field-configs', { headers }),
        axios.get(API + '/admin/users', { headers }),
        axios.get(API + '/admin/departments', { headers }),
        axios.get(API + '/admin/department-module-access', { headers }),
      ]);
      
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
      setDeptModuleAccess(deptAccessRes.data);
      
      // Group field configs by module
      const grouped = {};
      fieldsRes.data.forEach(config => {
        if (!grouped[config.module]) grouped[config.module] = [];
        grouped[config.module].push(config);
      });
      setFieldConfigs(grouped);
      setAvailableModules(Object.keys(grouped));
      
      const firstRole = rolesRes.data.find(r => r.name !== 'admin');
      if (firstRole) setSelectedRole(firstRole.name);
      
      // Load master data
      fetchMasterData();
    } catch (err) {
      toast.error('Failed to load admin settings');
    } finally {
      setLoading(false);
    }
  }
  
  async function fetchMasterData() {
    setLoadingMasters(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      const [brandsRes, subBrandsRes, categoriesRes, menuRes, subMenuRes, companiesRes] = await Promise.all([
        axios.get(API + '/masters/brands', { headers }),
        axios.get(API + '/masters/sub-brands', { headers }),
        axios.get(API + '/masters/categories', { headers }),
        axios.get(API + '/masters/menu', { headers }),
        axios.get(API + '/masters/sub-menu', { headers }),
        axios.get(API + '/masters/companies', { headers }),
      ]);
      
      setMasterBrands(brandsRes.data.brands || []);
      setMasterSubBrands(subBrandsRes.data.sub_brands || []);
      setMasterCategories(categoriesRes.data.categories || []);
      setMasterMenu(menuRes.data.menu_items || []);
      setMasterSubMenu(subMenuRes.data.sub_menus || []);
      setMasterCompanies(companiesRes.data.companies || []);
    } catch {
      // Master data load failed — UI will show empty state.
    } finally {
      setLoadingMasters(false);
    }
  }
  
  function openMasterModal(type, item = null) {
    setMasterModalType(type);
    setEditingMaster(item);
    if (type === 'sub_brand') {
      setMasterForm(item ? { 
        name: item.name, 
        description: item.description || '', 
        is_active: item.is_active,
        brand_id: item.brand_id || ''
      } : { name: '', description: '', is_active: true, brand_id: '' });
    } else if (type === 'sub_menu') {
      setMasterForm(item ? {
        name: item.name,
        description: item.description || '',
        is_active: item.is_active,
        category_id: item.category_id || '',
        menu_group_id: item.menu_group_id || ''
      } : { name: '', description: '', is_active: true, category_id: '', menu_group_id: '' });
    } else {
      setMasterForm(item ? { name: item.name, description: item.description || '', is_active: item.is_active } : { name: '', description: '', is_active: true });
    }
    setShowMasterModal(true);
  }
  
  async function saveMasterItem() {
    if (!masterForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    // For sub_brand, require brand_id
    if (masterModalType === 'sub_brand' && !masterForm.brand_id) {
      toast.error('Please select a parent brand');
      return;
    }
    
    // For sub_menu, require category_id and menu_group_id
    if (masterModalType === 'sub_menu' && (!masterForm.category_id || !masterForm.menu_group_id)) {
      toast.error('Please select a category and menu group');
      return;
    }
    
    setSavingMaster(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      let endpoint;
      if (masterModalType === 'brand') endpoint = '/masters/brands';
      else if (masterModalType === 'sub_brand') endpoint = '/masters/sub-brands';
      else if (masterModalType === 'category') endpoint = '/masters/categories';
      else if (masterModalType === 'menu') endpoint = '/masters/menu';
      else if (masterModalType === 'company') endpoint = '/masters/companies';
      else endpoint = '/masters/sub-menu';
      
      let payload;
      if (masterModalType === 'sub_brand') {
        payload = { name: masterForm.name, description: masterForm.description, is_active: masterForm.is_active, brand_id: masterForm.brand_id };
      } else if (masterModalType === 'sub_menu') {
        payload = { name: masterForm.name, description: masterForm.description, is_active: masterForm.is_active, category_id: masterForm.category_id, menu_group_id: masterForm.menu_group_id };
      } else {
        payload = masterForm;
      }
      
      const typeLabels = { brand: 'Parent Brand', sub_brand: 'Sub Brand', category: 'Category', menu: 'Menu Group', sub_menu: 'Sub Menu', company: 'Company' };
      if (editingMaster) {
        await axios.put(API + endpoint + '/' + editingMaster.id, payload, { headers });
        toast.success(`${typeLabels[masterModalType]} updated`);
      } else {
        await axios.post(API + endpoint, payload, { headers });
        toast.success(`${typeLabels[masterModalType]} created`);
      }
      
      setShowMasterModal(false);
      fetchMasterData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingMaster(false);
    }
  }
  
  async function deleteMasterItem(type, id, name) {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      let endpoint;
      if (type === 'brand') endpoint = `/masters/brands/${id}`;
      else if (type === 'sub_brand') endpoint = `/masters/sub-brands/${id}`;
      else if (type === 'category') endpoint = `/masters/categories/${id}`;
      else if (type === 'menu') endpoint = `/masters/menu/${id}`;
      else if (type === 'company') endpoint = `/masters/companies/${id}`;
      else endpoint = `/masters/sub-menu/${id}`;
      
      await axios.delete(API + endpoint, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Deleted successfully');
      fetchMasterData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  }
  
  function getPermissionForRole(roleName) {
    return permissions.find(p => p.role_name === roleName) || { tab_access: {}, action_permissions: {} };
  }
  
  async function updatePermission(roleName, type, key, value) {
    const perm = getPermissionForRole(roleName);
    const role = roles.find(r => r.name === roleName);
    const updated = {
      role_id: role?.id || roleName,
      tab_access: { ...perm.tab_access },
      action_permissions: { ...perm.action_permissions },
    };
    if (type === 'tab') updated.tab_access[key] = value;
    else updated.action_permissions[key] = value;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(API + '/admin/permissions/' + roleName, updated, { headers: { Authorization: 'Bearer ' + token } });
      const newPerms = permissions.map(p => p.role_name === roleName ? { ...p, ...updated } : p);
      if (!permissions.find(p => p.role_name === roleName)) newPerms.push({ role_name: roleName, ...updated });
      setPermissions(newPerms);
      toast.success('Permission updated');
    } catch (err) {
      toast.error('Failed to update permission');
    } finally {
      setSaving(false);
    }
  }
  
  async function updateFieldConfig(module, fieldName, isMandatory) {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(API + '/admin/field-configs/' + module + '/' + fieldName, {
        field_name: fieldName,
        is_mandatory: isMandatory,
        applies_to_roles: []
      }, { headers: { Authorization: 'Bearer ' + token } });
      
      const updated = { ...fieldConfigs };
      if (updated[module]) {
        updated[module] = updated[module].map(f => f.field_name === fieldName ? { ...f, is_mandatory: isMandatory } : f);
      }
      setFieldConfigs(updated);
      toast.success('Field configuration updated');
    } catch (err) {
      toast.error('Failed to update field config');
    } finally {
      setSaving(false);
    }
  }
  
  async function updateUserRole(userId, newRole) {
    try {
      const token = localStorage.getItem('token');
      await axios.put(API + '/admin/users/' + userId + '/role?role=' + newRole, {}, { headers: { Authorization: 'Bearer ' + token } });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (err) {
      toast.error('Failed to update user role');
    }
  }
  
  async function updateUserDepartment(userId, deptId) {
    try {
      const token = localStorage.getItem('token');
      await axios.put(API + '/admin/users/' + userId + '/department?department_id=' + (deptId || ''), {}, { headers: { Authorization: 'Bearer ' + token } });
      setUsers(users.map(u => u.id === userId ? { ...u, department_id: deptId || null } : u));
      toast.success('User department updated');
    } catch (err) {
      toast.error('Failed to update user department');
    }
  }
  
  async function saveDepartment() {
    if (!deptForm.name.trim()) { toast.error('Department name is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingDept) {
        await axios.put(API + '/admin/departments/' + editingDept.id, deptForm, { headers: { Authorization: 'Bearer ' + token } });
        setDepartments(departments.map(d => d.id === editingDept.id ? { ...d, ...deptForm } : d));
        toast.success('Department updated');
      } else {
        const res = await axios.post(API + '/admin/departments', deptForm, { headers: { Authorization: 'Bearer ' + token } });
        setDepartments([...departments, res.data]);
        toast.success('Department created');
      }
      setShowDeptModal(false);
      setEditingDept(null);
      setDeptForm({ name: '', description: '', color: '#3B82F6' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  }
  
  async function deleteDepartment(deptId) {
    if (!confirm('Are you sure you want to delete this department?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(API + '/admin/departments/' + deptId, { headers: { Authorization: 'Bearer ' + token } });
      setDepartments(departments.filter(d => d.id !== deptId));
      toast.success('Department deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete department');
    }
  }
  
  async function updateDeptModuleAccess(deptId, module, action, value) {
    // Atomic PATCH — backend writes `module_access.<module>.<action>` via `$set`
    // so parallel clicks on V/C/E/D never clobber each other. Returns the
    // freshly-persisted `module_access` which we drop into state as the
    // source of truth.
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        API + '/admin/department-module-access/' + deptId + '/toggle',
        { module, action, value },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const nextModuleAccess = res.data?.module_access || {};
      setDeptModuleAccess(prev => {
        const others = prev.filter(a => a.department_id !== deptId);
        return [...others, { department_id: deptId, module_access: nextModuleAccess }];
      });
      toast.success('Module access updated');
    } catch (err) {
      toast.error('Failed to update module access');
    } finally {
      setSaving(false);
    }
  }

  async function grantAllModuleActions(deptId, module, value) {
    // "All ✓" button — single atomic write sets V/C/E/D = value on one module.
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        API + '/admin/department-module-access/' + deptId + '/grant-all',
        { module, value },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const nextModuleAccess = res.data?.module_access || {};
      setDeptModuleAccess(prev => {
        const others = prev.filter(a => a.department_id !== deptId);
        return [...others, { department_id: deptId, module_access: nextModuleAccess }];
      });
      toast.success(value ? 'All actions granted' : 'All actions revoked');
    } catch (err) {
      toast.error('Failed to update module access');
    } finally {
      setSaving(false);
    }
  }
  
  function getDeptAccess(deptId, module, action) {
    const access = deptModuleAccess.find(a => a.department_id === deptId);
    return access?.module_access?.[module]?.[action] || false;
  }
  
  function getDeptById(deptId) {
    return departments.find(d => d.id === deptId);
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Loading admin settings...</span>
      </div>
    );
  }
  
  const selectedPerm = getPermissionForRole(selectedRole);
  
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Admin Settings
          </h1>
          <p className="text-slate-600 mt-1">Manage mandatory fields, departments, and access control</p>
        </div>
        <Button onClick={fetchAllData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>
      
      <Tabs defaultValue="company-profile" className="flex gap-6" orientation="vertical">
        {/* Vertical Tab List */}
        <TabsList className="flex flex-col h-fit bg-gray-50 p-2 rounded-lg border min-w-[200px] space-y-1">
          <TabsTrigger value="company-profile" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Store className="w-4 h-4" />
            Company Profile
          </TabsTrigger>
          <TabsTrigger value="employee-management" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="employee-management-tab">
            <UserCog className="w-4 h-4" />
            Employee Management
          </TabsTrigger>
          <TabsTrigger value="fields" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <FileText className="w-4 h-4" />
            Mandatory Fields
          </TabsTrigger>
          <TabsTrigger value="master-data" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Database className="w-4 h-4" />
            Master Data
          </TabsTrigger>
          <TabsTrigger value="departments" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Building2 className="w-4 h-4" />
            Departments
          </TabsTrigger>
          <TabsTrigger value="module-access" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Shield className="w-4 h-4" />
            Module Access
          </TabsTrigger>
          <TabsTrigger value="permissions" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Settings className="w-4 h-4" />
            Role Permissions
          </TabsTrigger>
          <TabsTrigger value="users" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="margin-master" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="margin-master-tab">
            <Calculator className="w-4 h-4" />
            Margin Master
          </TabsTrigger>
          <TabsTrigger value="store-logins" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="store-logins-tab">
            <Store className="w-4 h-4" />
            Store Logins
          </TabsTrigger>
          <TabsTrigger value="category-images" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-orange-600 data-[state=active]:text-white" data-testid="category-images-tab">
            <ImageIcon className="w-4 h-4" />
            Category Images
          </TabsTrigger>
          <TabsTrigger value="po-approvers" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="po-approvers-tab">
            <FileCheck className="w-4 h-4" />
            PO Approvers
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="w-full justify-start gap-2 px-3 py-2.5 data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="maintenance-tab">
            <Database className="w-4 h-4" />
            Database Cleanup
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Content Area */}
        <div className="flex-1 min-w-0">
        {/* ===================== COMPANY PROFILE TAB ===================== */}
        <TabsContent value="company-profile" className="mt-0">
          <CompanyProfileForm />
        </TabsContent>
        
        {/* ===================== EMPLOYEE MANAGEMENT TAB ===================== */}
        <TabsContent value="employee-management" className="mt-0">
          <EmployeeManagement />
        </TabsContent>
        
        {/* ===================== MANDATORY FIELDS TAB ===================== */}
        <TabsContent value="fields" className="space-y-4 mt-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Mandatory Field Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Configure which fields are mandatory for each module. Admin users are not bound by these rules.
              </p>
              
              {/* Module Tabs */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Select Module</Label>
                <div className="flex flex-wrap gap-2">
                  {availableModules.map(module => {
                    const config = MODULE_CONFIG[module] || { name: module, icon: FileText, color: 'gray' };
                    const Icon = config.icon;
                    const isSelected = selectedModule === module;
                    return (
                      <Button
                        key={module}
                        variant={isSelected ? 'default' : 'outline'}
                        onClick={() => setSelectedModule(module)}
                        className="gap-2"
                        size="sm"
                      >
                        <Icon className="w-4 h-4" />
                        {config.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              {/* Field Config Grid */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-4">
                  {(() => {
                    const config = MODULE_CONFIG[selectedModule] || { name: selectedModule, icon: FileText };
                    const Icon = config.icon;
                    return <Icon className="w-5 h-5 text-blue-600" />;
                  })()}
                  <h3 className="font-semibold text-gray-800">
                    {MODULE_CONFIG[selectedModule]?.name || selectedModule} Fields
                  </h3>
                  <Badge variant="outline" className="ml-auto">
                    {(fieldConfigs[selectedModule] || []).length} fields
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(fieldConfigs[selectedModule] || []).map(field => (
                    <div
                      key={field.field_name}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        field.is_mandatory ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <Label className="font-medium text-sm truncate block">{field.display_name}</Label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {field.is_mandatory ? 'Required' : 'Optional'}
                          {field.conditional && <span className="text-orange-600"> (conditional)</span>}
                        </p>
                      </div>
                      <Switch
                        checked={field.is_mandatory}
                        onCheckedChange={(v) => updateFieldConfig(selectedModule, field.field_name, v)}
                        disabled={saving}
                      />
                    </div>
                  ))}
                </div>
                
                {(fieldConfigs[selectedModule] || []).length === 0 && (
                  <div className="text-center py-8 text-gray-500">No field configurations found for this module</div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Summary Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                All Modules Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {availableModules.map(module => {
                  const config = MODULE_CONFIG[module] || { name: module, icon: FileText };
                  const Icon = config.icon;
                  const moduleFields = fieldConfigs[module] || [];
                  const mandatoryCount = moduleFields.filter(f => f.is_mandatory).length;
                  return (
                    <div key={module} className="p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100" onClick={() => setSelectedModule(module)}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-gray-600" />
                        <span className="font-medium text-sm truncate">{config.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{moduleFields.length} fields</span>
                        <Badge className={mandatoryCount > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}>
                          {mandatoryCount} req
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ===================== MASTER DATA TAB ===================== */}
        <TabsContent value="master-data" className="space-y-4 mt-0">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  Master Data Management
                </CardTitle>
                <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={() => setShowUploadHistory(true)} data-testid="upload-history-btn">
                  <Clock className="w-3 h-3" /> Upload History
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-6">
                Manage dropdown values for Brands, Categories, and Menu Groups. These values appear in product creation forms.
              </p>
              
              {loadingMasters ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  <span className="ml-2">Loading master data...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <MasterDataSection
                    title="Parent Brands" icon={Tag} color="blue" items={masterBrands} masterType="brand"
                    onAdd={() => openMasterModal('brand')}
                    onEdit={(item) => openMasterModal('brand', item)}
                    onDelete={(id, name) => deleteMasterItem('brand', id, name)}
                    onUpload={() => { setBulkUploadType('brand'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                  />
                  <MasterDataSection
                    title="Sub Brands" icon={Tag} color="indigo" items={masterSubBrands} masterType="sub_brand"
                    onAdd={() => openMasterModal('sub_brand')}
                    onEdit={(item) => openMasterModal('sub_brand', item)}
                    onDelete={(id, name) => deleteMasterItem('sub_brand', id, name)}
                    onUpload={() => { setBulkUploadType('sub_brand'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                    renderItem={(item) => (
                      <span className="text-sm truncate">{item.name} <span className="text-xs text-gray-500">({item.brand_name})</span></span>
                    )}
                  />
                  <MasterDataSection
                    title="Categories" icon={Folder} color="green" items={masterCategories} masterType="category"
                    onAdd={() => openMasterModal('category')}
                    onEdit={(item) => openMasterModal('category', item)}
                    onDelete={(id, name) => deleteMasterItem('category', id, name)}
                    onUpload={() => { setBulkUploadType('category'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                  />
                  <MasterDataSection
                    title="Menu Groups" icon={Menu} color="purple" items={masterMenu} masterType="menu"
                    onAdd={() => openMasterModal('menu')}
                    onEdit={(item) => openMasterModal('menu', item)}
                    onDelete={(id, name) => deleteMasterItem('menu', id, name)}
                    onUpload={() => { setBulkUploadType('menu'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                  />
                  <MasterDataSection
                    title="Sub Menus" icon={Layers} color="orange" items={masterSubMenu} masterType="sub_menu"
                    onAdd={() => openMasterModal('sub_menu')}
                    onEdit={(item) => openMasterModal('sub_menu', item)}
                    onDelete={(id, name) => deleteMasterItem('sub_menu', id, name)}
                    onUpload={() => { setBulkUploadType('sub_menu'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                    renderItem={(item) => (
                      <span className="text-sm truncate">{item.name} <span className="text-xs text-gray-500">({item.category_name} &gt; {item.menu_group_name})</span></span>
                    )}
                  />
                  <MasterDataSection
                    title="Companies" icon={Building2} color="purple" items={masterCompanies} masterType="company"
                    onAdd={() => openMasterModal('company')}
                    onEdit={(item) => openMasterModal('company', item)}
                    onDelete={(id, name) => deleteMasterItem('company', id, name)}
                    onUpload={() => { setBulkUploadType('company'); setShowBulkUpload(true); }}
                    onResetComplete={fetchMasterData}
                    renderItem={(item) => (
                      <span className="text-sm truncate">{item.name}</span>
                    )}
                  />
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
            <strong>Note:</strong> Values added here will appear as dropdown options in the Master Catalogue product creation form. Only Admin can manage these values.
          </div>
        </TabsContent>
        
        {/* ===================== DEPARTMENTS TAB ===================== */}
        <TabsContent value="departments" className="space-y-4 mt-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  Department Management
                </span>
                <Button onClick={() => { setEditingDept(null); setDeptForm({ name: '', description: '', color: '#3B82F6' }); setShowDeptModal(true); }} className="gap-2" size="sm">
                  <Plus className="w-4 h-4" />
                  Add Department
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Create departments to organize users and control module access by team.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {departments.map(dept => {
                  const usersInDept = users.filter(u => u.department_id === dept.id).length;
                  return (
                    <div key={dept.id} className="p-4 rounded-lg border-2 transition-all hover:shadow-md" style={{ borderColor: dept.color + '40', backgroundColor: dept.color + '10' }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />
                          <h3 className="font-semibold text-gray-800">{dept.name}</h3>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingDept(dept); setDeptForm({ name: dept.name, description: dept.description || '', color: dept.color }); setShowDeptModal(true); }}>
                            <Settings className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-800" onClick={() => deleteDepartment(dept.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">{dept.description || 'No description'}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">{usersInDept} user{usersInDept !== 1 ? 's' : ''}</Badge>
                      </div>
                    </div>
                  );
                })}
                
                {departments.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No departments created yet. Click "Add Department" to create one.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ===================== MODULE ACCESS TAB ===================== */}
        <TabsContent value="module-access" className="space-y-4 mt-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                Department Module Access Control
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Define which modules each department can access. Admin role always has full access.
              </p>
              
              {departments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Create departments first to configure module access.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left font-semibold border-r w-40">Module</th>
                        {departments.map(dept => (
                          <th key={dept.id} className="px-2 py-2 text-center font-semibold border-r" style={{ minWidth: '120px' }}>
                            <div className="flex items-center justify-center gap-1">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color }} />
                              {dept.name}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(MODULE_CONFIG).map(([moduleId, config]) => (
                        <tr key={moduleId} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-3 border-r font-medium">
                            <div className="flex items-center gap-2">
                              <config.icon className="w-4 h-4 text-gray-500" />
                              {config.name}
                            </div>
                          </td>
                          {departments.map(dept => {
                            const allGranted = ['view','create','edit','delete'].every(
                              a => getDeptAccess(dept.id, moduleId, a)
                            );
                            return (
                              <td key={dept.id} className="px-2 py-2 border-r">
                                <div className="flex flex-wrap gap-1 justify-center items-center">
                                  {['view', 'create', 'edit', 'delete'].map(action => (
                                    <button
                                      key={action}
                                      data-testid={`dept-perm-${dept.name.toLowerCase().replace(/\s+/g,'-')}-${moduleId}-${action}`}
                                      onClick={() => updateDeptModuleAccess(dept.id, moduleId, action, !getDeptAccess(dept.id, moduleId, action))}
                                      className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                        getDeptAccess(dept.id, moduleId, action)
                                          ? 'bg-green-100 text-green-800 border border-green-300'
                                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                                      }`}
                                      disabled={saving}
                                    >
                                      {action.charAt(0).toUpperCase()}
                                    </button>
                                  ))}
                                  <button
                                    data-testid={`dept-perm-all-${dept.name.toLowerCase().replace(/\s+/g,'-')}-${moduleId}`}
                                    onClick={() => grantAllModuleActions(dept.id, moduleId, !allGranted)}
                                    title={allGranted ? 'Revoke all actions' : 'Grant all actions'}
                                    className={`ml-1 px-2 py-0.5 text-[10px] rounded border transition-colors ${
                                      allGranted
                                        ? 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700'
                                        : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                                    }`}
                                    disabled={saving}
                                  >
                                    {allGranted ? 'All ✓' : 'All'}
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 flex gap-4 text-xs text-gray-500 flex-wrap">
                    <span><strong>V</strong> = View</span>
                    <span><strong>C</strong> = Create</span>
                    <span><strong>E</strong> = Edit</span>
                    <span><strong>D</strong> = Delete</span>
                    <span><strong>All</strong> = Grant/revoke all 4 actions on this module in one click</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ===================== ROLE PERMISSIONS TAB ===================== */}
        <TabsContent value="permissions" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-600" />
                Configure Role Permissions (Purchase Orders)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                {nonAdminRoles.map(role => (
                  <Button key={role.name} variant={selectedRole === role.name ? 'default' : 'outline'} onClick={() => setSelectedRole(role.name)} className="capitalize">
                    {role.name}
                  </Button>
                ))}
              </div>
              
              {selectedRole && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Tab Access</h3>
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      {Object.entries(TAB_LABELS).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between">
                          <Label className="text-sm">{label}</Label>
                          <Switch checked={selectedPerm.tab_access[key] !== false} onCheckedChange={(v) => updatePermission(selectedRole, 'tab', key, v)} disabled={saving} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Action Permissions</h3>
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      {Object.entries(ACTION_LABELS).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between">
                          <Label className="text-sm">{label}</Label>
                          <Switch checked={selectedPerm.action_permissions[key] === true} onCheckedChange={(v) => updatePermission(selectedRole, 'action', key, v)} disabled={saving} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Note:</strong> Admin role has full access to all tabs and actions. These settings only apply to non-admin roles.
          </div>
        </TabsContent>
        
        {/* ===================== USERS TAB ===================== */}
        <TabsContent value="users" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                User Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Role</th>
                      <th className="px-4 py-3 text-left font-semibold">Department</th>
                      <th className="px-4 py-3 text-left font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map(user => {
                      const userDept = getDeptById(user.department_id);
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{user.name}</td>
                          <td className="px-4 py-3 text-gray-600">{user.email}</td>
                          <td className="px-4 py-3">
                            <select value={user.role} onChange={(e) => updateUserRole(user.id, e.target.value)} className="border rounded px-2 py-1 text-sm">
                              {roles.map(r => (<option key={r.name} value={r.name}>{r.name}</option>))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={user.department_id || ''}
                              onChange={(e) => updateUserDepartment(user.id, e.target.value)}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              <option value="">-- No Department --</option>
                              {departments.map(d => (<option key={d.id} value={d.id}>{d.name}</option>))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            {userDept && (
                              <Badge style={{ backgroundColor: userDept.color + '20', color: userDept.color, borderColor: userDept.color }} className="border">
                                {userDept.name}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===================== DATABASE MAINTENANCE TAB ===================== */}
        <TabsContent value="margin-master" className="space-y-4 mt-0">
          <MarginMasterAdmin />
        </TabsContent>

        <TabsContent value="po-approvers" className="space-y-4 mt-0">
          <POApproversSettings />
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-4 mt-0">
          <OrderStatusRepair />
          <DatabaseCleanup />
        </TabsContent>

        <TabsContent value="store-logins" className="space-y-4 mt-0">
          <StoreLoginsAdmin />
        </TabsContent>

        <TabsContent value="category-images" className="space-y-4 mt-0">
          <CategoryImagesAdmin />
        </TabsContent>
        </div>
      </Tabs>
      
      {/* Department Modal */}
      <DepartmentModal
        open={showDeptModal}
        onOpenChange={setShowDeptModal}
        editingDept={editingDept}
        deptForm={deptForm}
        setDeptForm={setDeptForm}
        saving={saving}
        onSave={saveDepartment}
      />
      
      {/* Master Data Modal */}
      <MasterDataModal
        open={showMasterModal}
        onOpenChange={setShowMasterModal}
        masterModalType={masterModalType}
        editingMaster={editingMaster}
        masterForm={masterForm}
        setMasterForm={setMasterForm}
        savingMaster={savingMaster}
        onSave={saveMasterItem}
        masterBrands={masterBrands}
        masterCategories={masterCategories}
        masterMenu={masterMenu}
      />
      
      {/* Bulk Upload Dialog */}
      <MasterBulkUpload
        masterType={bulkUploadType || 'brand'}
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={fetchMasterData}
      />
      
      {/* Upload History Dialog */}
      <MasterUploadHistory
        open={showUploadHistory}
        onClose={() => setShowUploadHistory(false)}
      />
    </div>
  );
}

export default AdminSettings;
